import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  FileText,
  Receipt,
  BookOpen,
  Users,
  PieChart,
  Settings as SettingsIcon,
  LogOut,
  ShieldCheck,
  Building2,
  Lock,
  ListTree,
  Coins,
  Sparkles,
  Images,
  Wallet,
  Link2,
  FileStack,
  HeartHandshake,
  Building,
} from "lucide-react";

const navItems = [
  { to: "/app", icon: LayoutDashboard, label: "ダッシュボード", end: true, testid: "nav-dashboard" },
  { to: "/app/billing", icon: FileStack, label: "請求管理", testid: "nav-billing", caps: ["billing", "accounting"] },
  { to: "/app/billing/payments", icon: Wallet, label: "入金消込", testid: "nav-payments", caps: ["billing", "accounting"] },
  { to: "/app/billing/accounting-link", icon: Link2, label: "会計連携", testid: "nav-accounting-link", caps: ["accounting"] },
  { to: "/app/welfare", icon: HeartHandshake, label: "国保連請求", testid: "nav-welfare", caps: ["accounting", "billing"] },
  { to: "/app/expenses", icon: Receipt, label: "経費", testid: "nav-expenses", caps: ["accounting", "photo"] },
  { to: "/app/receipts", icon: Images, label: "レシート帳", testid: "nav-receipts", caps: ["photo"] },
  { to: "/app/journal", icon: BookOpen, label: "仕訳", testid: "nav-journal", caps: ["accounting"] },
  { to: "/app/ai", icon: Sparkles, label: "AI仕訳", testid: "nav-ai", caps: ["photo"] },
  { to: "/app/customers", icon: Users, label: "取引先", testid: "nav-customers", caps: ["billing", "accounting"] },
  { to: "/app/wages", icon: Coins, label: "工賃台帳", testid: "nav-wages", caps: ["accounting"] },
  { to: "/app/masters", icon: ListTree, label: "会計マスター", testid: "nav-masters", caps: ["accounting"] },
  { to: "/app/fixed-assets", icon: Building, label: "固定資産", testid: "nav-fixed-assets", caps: ["accounting"] },
  { to: "/app/reports", icon: PieChart, label: "レポート", testid: "nav-reports", caps: ["accounting"] },
  { to: "/app/security", icon: Lock, label: "セキュリティ", testid: "nav-security" },
  { to: "/app/settings", icon: SettingsIcon, label: "設定", testid: "nav-settings" },
];

const orgNavItem = { to: "/app/organization", icon: Building2, label: "会社・メンバー", testid: "nav-organization" };
const adminNavItem = { to: "/app/admin", icon: ShieldCheck, label: "プラットフォーム管理", testid: "nav-admin" };

export default function DashboardLayout({ children }) {
  const { user, logout, activeTenantId, switchTenant } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const tenants = user?.tenants || [];
  const activeTenant = tenants.find((t) => t.id === activeTenantId) || null;
  const isPlatformAdmin = user?.is_platform_admin || user?.role === "admin";
  const isCompanyAdmin = activeTenant?.role === "company_admin";

  const caps = activeTenant?.capabilities || {};
  const hasCap = (item) => !item.caps || item.caps.some((c) => caps[c]);
  let items = navItems.filter(hasCap);
  if (isCompanyAdmin) items = [...items, orgNavItem];
  if (isPlatformAdmin) items = [...items, adminNavItem];

  const TenantSwitcher = () => (
    <div className="px-4 py-3 border-b border-stone-200">
      <label className="text-[10px] uppercase tracking-[0.1em] font-semibold text-stone-500">利用中の会社</label>
      <select
        data-testid="tenant-switcher"
        value={activeTenantId || ""}
        onChange={(e) => switchTenant(e.target.value)}
        className="mt-1 w-full rounded-md border border-stone-200 bg-white px-2 py-2 text-sm text-stone-800 focus:border-emerald-400 focus:outline-none"
      >
        {tenants.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}（{t.role_label}）
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-stone-50">
      <aside className="hidden md:flex w-64 flex-col border-r border-stone-200 bg-white">
        <div className="flex items-center gap-2 px-6 py-6 border-b border-stone-200">
          <Link to="/" className="flex items-center gap-2" data-testid="sidebar-logo">
            <img src="/logo.png" alt="PRC会計" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-bold text-stone-900 text-base">PRC会計</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500">SaaS</div>
            </div>
          </Link>
        </div>

        {tenants.length > 0 && <TenantSwitcher />}

        <nav className="flex-1 p-4 space-y-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-emerald-50 text-emerald-900"
                    : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                }`
              }
            >
              <item.icon className="h-4 w-4" strokeWidth={1.75} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-stone-200 p-4">
          <div className="px-2 mb-3">
            <p className="text-xs text-stone-500 uppercase tracking-[0.1em] font-semibold">アカウント</p>
            <p className="mt-1 text-sm font-medium text-stone-900 truncate" data-testid="sidebar-user-name">
              {user?.name || "ユーザー"}
            </p>
            <p className="text-xs text-stone-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.75} />
            ログアウト
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between">
        <Link to="/app" className="flex items-center gap-2">
          <img src="/logo.png" alt="PRC会計" className="h-8 w-8 object-contain" />
          <div className="font-display font-bold text-stone-900">PRC会計</div>
        </Link>
        <button
          onClick={handleLogout}
          data-testid="logout-button-mobile"
          className="text-stone-600"
        >
          <LogOut className="h-5 w-5" />
        </button>
      </div>

      <main className="flex-1 overflow-x-hidden pt-14 md:pt-0">
        <div className="md:hidden border-b border-stone-200 bg-white px-4 py-2 overflow-x-auto flex gap-2">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`${item.testid}-mobile`}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ${
                  isActive ? "bg-emerald-900 text-white" : "bg-stone-100 text-stone-700"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
        <div className="p-6 md:p-8">{children}</div>
      </main>
    </div>
  );
}
